import React, { useState, useEffect, useCallback } from 'react';
import {
    Show,
    useTranslate,
    useRecordContext,
    useRedirect,
    useNotify,
    useRefresh,
    usePermissions,
    EditButton,
    TopToolbar,
    ListButton,
    ReferenceField,
    TextField,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField as MuiTextField,
    Stack,
    Divider,
    Alert,
    CircularProgress,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    ArrowBack,
    EventAvailable,
    Cancel as CancelIcon,
    PriceCheck,
    Refresh,
    LockOpen,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';
import { hasPermission } from '../../utils/hasPermission';
import { ReservationStatusChip } from './ReservationsList';

const ShowActions = () => (
    <TopToolbar>
        <ListButton />
        <EditButton />
    </TopToolbar>
);

const fmt = (v: any): string => {
    if (v === null || v === undefined) return '-';
    if (typeof v === 'boolean') return v ? '✓' : '✗';
    if (Array.isArray(v)) return v.length === 0 ? '-' : v.join(', ');
    return String(v);
};

const fmtCurrency = (v: any): string => {
    if (v === null || v === undefined || v === '') return '-';
    const n = Number(v);
    if (Number.isNaN(n)) return '-';
    return `HKD ${n.toFixed(2)}`;
};

const fmtDate = (v: any): string => {
    if (!v) return '-';
    try {
        return new Date(v).toLocaleString();
    } catch {
        return String(v);
    }
};

const Row: React.FC<{ label: string; children: React.ReactNode; color?: 'default' | 'error' | 'muted' }> = ({ label, children, color }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
        <Box sx={{ minWidth: { xs: '100%', sm: 220 }, color: 'text.secondary', fontSize: '0.875rem' }}>
            {label}
        </Box>
        <Box
            sx={{
                flex: 1,
                fontSize: '0.875rem',
                color: color === 'error' ? 'error.main' : color === 'muted' ? 'text.secondary' : 'inherit',
            }}
        >
            {children}
        </Box>
    </Box>
);

const WalletHoldStatusChip: React.FC<{ status: string | null | undefined }> = ({ status }) => {
    const translate = useTranslate();
    if (!status) return <span>-</span>;
    const colorMap: Record<string, 'default' | 'warning' | 'primary' | 'success' | 'info'> = {
        pending: 'warning',
        held: 'primary',
        released: 'success',
        captured: 'info',
    };
    return (
        <Chip
            size="small"
            color={colorMap[status] || 'default'}
            label={translate(`resources.reservations.walletHoldStatus.${status}`, { _: status })}
        />
    );
};

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    reasonRequired?: boolean;
    reasonPlaceholder?: string;
    confirmLabel: string;
    confirmColor?: 'primary' | 'error' | 'warning';
    submitting: boolean;
    onConfirm: (reason?: string) => void;
    onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open,
    title,
    message,
    reasonRequired = false,
    reasonPlaceholder,
    confirmLabel,
    confirmColor = 'primary',
    submitting,
    onConfirm,
    onCancel,
}) => {
    const [reason, setReason] = useState('');
    const translate = useTranslate();

    useEffect(() => {
        if (!open) setReason('');
    }, [open]);

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>{message}</Typography>
                <MuiTextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={reasonPlaceholder || translate('resources.reservations.show.actions.reasonPlaceholder')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    required={reasonRequired}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={submitting}>
                    {translate('ra.action.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color={confirmColor}
                    onClick={() => onConfirm(reason || undefined)}
                    disabled={submitting || (reasonRequired && !reason.trim())}
                >
                    {submitting ? <CircularProgress size={16} /> : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const ActionsCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { permissions } = usePermissions();

    const [submitting, setSubmitting] = useState(false);
    const [openDialog, setOpenDialog] = useState<null | 'cancel' | 'capture' | 'refresh' | 'forceRelease'>(null);

    if (!record) return null;

    const canWrite = hasPermission(permissions as string[] | null | undefined, 'reservations:write:all');
    const status = String(record.status || '');
    const walletHoldStatus = record.wallet_hold_status || record.walletHoldStatus;
    const transactionId = record.transaction_id || record.transactionId;

    const showCancel = canWrite && (status === 'confirmed' || status === 'active');
    const showCapture = canWrite && status === 'no_show' && walletHoldStatus === 'held';
    const showRefresh = canWrite && status === 'confirmed' && !transactionId;
    const showForceRelease =
        canWrite &&
        walletHoldStatus === 'held' &&
        ['cancelled', 'expired', 'no_show', 'completed'].includes(status);

    const hasAnyAction = showCancel || showCapture || showRefresh || showForceRelease;

    const handleAction = useCallback(
        async (kind: 'cancel' | 'capture' | 'refresh' | 'forceRelease', reason?: string) => {
            const id = String(record.id);
            setSubmitting(true);
            try {
                if (kind === 'cancel') {
                    await apiClient.post(`/api/backend/reservations/${id}/cancel`, { reason });
                    notify('resources.reservations.notifications.cancelled', { type: 'success' });
                } else if (kind === 'capture') {
                    await apiClient.post(`/api/backend/reservations/${id}/capture-noshow`, { reason });
                    notify('resources.reservations.notifications.noShowCaptured', { type: 'success' });
                } else if (kind === 'refresh') {
                    await apiClient.post(`/api/backend/reservations/${id}/reassign`, { reason });
                    notify('resources.reservations.notifications.reassigned', { type: 'success' });
                } else if (kind === 'forceRelease') {
                    await apiClient.post(`/api/backend/reservations/${id}/force-release-hold`, { reason });
                    notify('resources.reservations.notifications.holdReleased', { type: 'success' });
                }
                setOpenDialog(null);
                refresh();
            } catch (err: any) {
                const errKey =
                    kind === 'cancel'
                        ? 'resources.reservations.notifications.error.cancel'
                        : kind === 'capture'
                            ? 'resources.reservations.notifications.error.capture'
                            : kind === 'refresh'
                                ? 'resources.reservations.notifications.error.reassign'
                                : 'resources.reservations.notifications.error.forceRelease';
                const code = err?.body?.errorCode;
                if (code) {
                    notify(`errors.${code}`, { type: 'error' });
                } else {
                    notify(errKey, { type: 'error' });
                }
            } finally {
                setSubmitting(false);
            }
        },
        [record, notify, refresh]
    );

    if (!hasAnyAction) {
        return null;
    }

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservations.show.sections.summary')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    {showCancel && (
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => setOpenDialog('cancel')}
                        >
                            {translate('resources.reservations.show.actions.cancel')}
                        </Button>
                    )}
                    {showCapture && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<PriceCheck />}
                            onClick={() => setOpenDialog('capture')}
                        >
                            {translate('resources.reservations.show.actions.captureNoShow')}
                        </Button>
                    )}
                    {showRefresh && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<Refresh />}
                            onClick={() => setOpenDialog('refresh')}
                        >
                            {translate('resources.reservations.show.actions.refreshAssignment')}
                        </Button>
                    )}
                    {showForceRelease && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<LockOpen />}
                            onClick={() => setOpenDialog('forceRelease')}
                        >
                            {translate('resources.reservations.show.actions.forceReleaseHold')}
                        </Button>
                    )}
                </Stack>
            </CardContent>

            <ConfirmDialog
                open={openDialog === 'cancel'}
                title={translate('resources.reservations.show.actions.cancel')}
                message={translate('resources.reservations.show.actions.confirmCancel')}
                confirmLabel={translate('resources.reservations.show.actions.cancel')}
                confirmColor="error"
                submitting={submitting}
                reasonRequired
                onConfirm={(reason) => handleAction('cancel', reason)}
                onCancel={() => setOpenDialog(null)}
            />
            <ConfirmDialog
                open={openDialog === 'capture'}
                title={translate('resources.reservations.show.actions.captureNoShow')}
                message={translate('resources.reservations.show.actions.confirmCapture')}
                confirmLabel={translate('resources.reservations.show.actions.captureNoShow')}
                confirmColor="warning"
                submitting={submitting}
                onConfirm={(reason) => handleAction('capture', reason)}
                onCancel={() => setOpenDialog(null)}
            />
            <ConfirmDialog
                open={openDialog === 'refresh'}
                title={translate('resources.reservations.show.actions.refreshAssignment')}
                message={translate('resources.reservations.show.actions.confirmRefresh')}
                confirmLabel={translate('resources.reservations.show.actions.refreshAssignment')}
                confirmColor="primary"
                submitting={submitting}
                onConfirm={(reason) => handleAction('refresh', reason)}
                onCancel={() => setOpenDialog(null)}
            />
            <ConfirmDialog
                open={openDialog === 'forceRelease'}
                title={translate('resources.reservations.show.actions.forceReleaseHold')}
                message={translate('resources.reservations.show.actions.confirmForceRelease')}
                confirmLabel={translate('resources.reservations.show.actions.forceReleaseHold')}
                confirmColor="warning"
                submitting={submitting}
                onConfirm={(reason) => handleAction('forceRelease', reason)}
                onCancel={() => setOpenDialog(null)}
            />
        </Card>
    );
};

const PageHeader: React.FC = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    if (!record) return null;
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 }, flexWrap: 'wrap' }}>
            <IconButton
                onClick={() => redirect('/reservations')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('resources.reservations.list.title')}
            >
                <ArrowBack />
            </IconButton>
            <EventAvailable sx={{ color: '#009A75', fontSize: 32 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2rem' } }}>
                    {translate('resources.reservations.show.subtitle')}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', color: 'text.secondary' }}>
                    {String(record.id)}
                </Typography>
            </Box>
            <ReservationStatusChip status={record.status} />
        </Box>
    );
};

const SummaryCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const mode = record.reservation_mode || record.reservationMode;
    const access = record.access_mode || record.accessMode;
    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservations.show.sections.summary')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Row label={translate('resources.reservations.fields.id')}>
                            <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(record.id)}</span>
                        </Row>
                        <Row label={translate('resources.reservations.fields.status')}>
                            <ReservationStatusChip status={record.status} />
                        </Row>
                        <Row label={translate('resources.reservations.fields.siteId')}>
                            <ReferenceField record={record} source="site_id" reference="sites" link="show">
                                <TextField source="name" />
                            </ReferenceField>
                        </Row>
                        <Row label={translate('resources.reservations.fields.chargerId')}>
                            <ReferenceField record={record} source="charger_id" reference="chargers" link="show">
                                <TextField source="name" />
                            </ReferenceField>
                        </Row>
                        <Row label={translate('resources.reservations.fields.connectorId')}>{fmt(record.connector_id ?? record.connectorId)}</Row>
                        <Row label={translate('resources.reservations.fields.reservationMode')}>
                            {mode ? <Chip size="small" variant="outlined" label={translate(`resources.reservations.modes.${mode}`, { _: mode })} /> : '-'}
                        </Row>
                        <Row label={translate('resources.reservations.fields.accessMode')}>
                            {access ? <Chip size="small" variant="outlined" label={translate(`resources.reservations.accessModes.${access}`, { _: access })} /> : '-'}
                        </Row>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Row label={translate('resources.reservations.fields.appUserId')}>
                            <ReferenceField record={record} source="app_user_id" reference="app-users" link="show">
                                <TextField source="email" />
                            </ReferenceField>
                        </Row>
                        <Row label={translate('resources.reservations.fields.startTime')}>{fmtDate(record.start_time || record.startTime)}</Row>
                        <Row label={translate('resources.reservations.fields.slotEndAt')}>{fmtDate(record.slot_end_at || record.slotEndAt)}</Row>
                        <Row label={translate('resources.reservations.fields.holdUntil')}>{fmtDate(record.hold_until || record.holdUntil)}</Row>
                        <Row label={translate('resources.reservations.fields.durationMinutes')}>
                            {fmt(record.duration_minutes ?? record.durationMinutes)}
                        </Row>
                        <Row label={translate('resources.reservations.fields.policyId')}>
                            {record.policy_id || record.policyId ? (
                                <Link to={`/reservation-policies/${record.policy_id || record.policyId}/show`}>
                                    {String(record.policy_id || record.policyId).slice(-8)}
                                </Link>
                            ) : (
                                '-'
                            )}
                        </Row>
                        <Row label={translate('resources.reservations.fields.createdAt')}>{fmtDate(record.created_at || record.createdAt)}</Row>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const PolicySnapshotCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const snapshot = record.policy_snapshot || record.policySnapshot || {};
    if (!snapshot || Object.keys(snapshot).length === 0) {
        return null;
    }
    const keyMap: Record<string, string> = {
        reservation_mode: 'reservationMode',
        access_mode: 'accessMode',
        hold_window_minutes: 'holdMinutes',
        slot_duration_minutes: 'slotDurationMinutes',
        slot_granularity_minutes: 'slotGranularityMinutes',
        min_lead_minutes: 'minLeadMinutes',
        max_lead_minutes: 'maxLeadMinutes',
        max_active_per_user: 'maxActivePerUser',
        cancel_window_minutes: 'cancelWindowMinutes',
        modify_window_minutes: 'modifyWindowMinutes',
        max_modifications: 'maxModifications',
        fee_policy: 'feePolicy',
        no_show_penalty_amount: 'noShowPenaltyAmount',
        allowed_membership_levels: 'allowedMembershipLevels',
        allowed_user_group_ids: 'allowedUserGroupIds',
        allowed_group_ids: 'allowedUserGroupIds',
        require_wallet_balance: 'requireWalletBalance',
    };

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservations.show.sections.policySnapshot')}
                </Typography>
                {Object.entries(snapshot).map(([k, v]) => {
                    const i18nKey = keyMap[k];
                    const label = i18nKey
                        ? translate(`resources.reservations.policySnapshot.${i18nKey}`, { _: k })
                        : k;
                    return (
                        <Row key={k} label={label}>
                            {fmt(v)}
                        </Row>
                    );
                })}
                <Alert severity="info" sx={{ mt: 2 }}>
                    {translate('resources.reservations.tooltips.policySnapshotReadOnly')}
                </Alert>
            </CardContent>
        </Card>
    );
};

const FeeAndWalletCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const [wallet, setWallet] = useState<any[] | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!record?.id) return;
        let cancelled = false;
        setLoading(true);
        // Wallet history queries app_transactions WHERE reference_type='reservation' AND reference_id=:id
        apiClient
            .get<any>(`/api/backend/app-transactions?reference_type=reservation&reference_id=${encodeURIComponent(String(record.id))}&limit=200&offset=0&sort=created_at&order=asc`)
            .then((r) => {
                if (cancelled) return;
                setWallet(Array.isArray(r) ? r : r?.data || []);
            })
            .catch(() => {
                if (cancelled) return;
                setWallet([]);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [record?.id]);

    if (!record) return null;
    const feePolicy = record.fee_policy || record.feePolicy;
    const feeAmount = record.fee_amount ?? record.feeAmount;
    const walletHoldAmount = record.wallet_hold_amount ?? record.walletHoldAmount;
    const walletHoldStatus = record.wallet_hold_status || record.walletHoldStatus;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservations.show.sections.feeAndWallet')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {translate('resources.reservations.fee.title')}
                        </Typography>
                        <Row label={translate('resources.reservations.fields.feePolicy')}>
                            {feePolicy ? translate(`resources.reservations.feePolicies.${feePolicy}`, { _: feePolicy }) : '-'}
                        </Row>
                        <Row label={translate('resources.reservations.fields.feeAmount')}>{fmtCurrency(feeAmount)}</Row>
                        <Row label={translate('resources.reservations.fields.walletHoldAmount')}>{fmtCurrency(walletHoldAmount)}</Row>
                        <Row label={translate('resources.reservations.fields.walletHoldStatus')}>
                            <WalletHoldStatusChip status={walletHoldStatus} />
                        </Row>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            {translate('resources.reservations.wallet.timeline')}
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : !wallet || wallet.length === 0 ? (
                            <Alert severity="info">{translate('resources.reservations.wallet.empty')}</Alert>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {wallet.map((entry, i) => {
                                    const type = entry.type || entry.transaction_type;
                                    const status = entry.status;
                                    // Map type+status to display label per R6-μ
                                    let labelKey = 'resources.reservations.wallet.types.' + (type || 'pre_auth');
                                    if (type === 'pre_auth' && status === 'hold') {
                                        labelKey = 'resources.reservations.wallet.reasons.hold';
                                    } else if (type === 'pre_auth' && status === 'released') {
                                        labelKey = 'resources.reservations.wallet.reasons.release';
                                    } else if (type === 'reservation_fee_applied') {
                                        labelKey = 'resources.reservations.wallet.reasons.apply';
                                    } else if (type === 'reservation_forfeit') {
                                        labelKey = 'resources.reservations.wallet.reasons.noshow_capture';
                                    }
                                    return (
                                        <Box
                                            key={entry.id || i}
                                            sx={{
                                                borderLeft: '3px solid #009A75',
                                                pl: 1.5,
                                                py: 0.75,
                                                backgroundColor: '#FAFAFA',
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {fmtDate(entry.created_at || entry.createdAt)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {translate(labelKey, { _: type || 'transaction' })}
                                                {entry.amount !== undefined && (
                                                    <> &middot; {fmtCurrency(entry.amount)}</>
                                                )}
                                                {entry.status && (
                                                    <> &middot; <Chip size="small" variant="outlined" label={translate(`resources.reservations.wallet.statuses.${entry.status}`, { _: entry.status })} /></>
                                                )}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const LinkedSessionCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const [session, setSession] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);

    const txnId = record?.transaction_id || record?.transactionId;

    useEffect(() => {
        if (!txnId) return;
        let cancelled = false;
        setLoading(true);
        apiClient
            .get<any>(`/api/backend/charging-sessions/${encodeURIComponent(String(txnId))}`)
            .then((r) => {
                if (cancelled) return;
                setSession(r?.data || r);
            })
            .catch(() => {
                if (cancelled) return;
                setSession(null);
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [txnId]);

    if (!record) return null;
    if (!txnId) {
        return null;
    }

    const costBreakdown = session?.cost_breakdown || session?.costBreakdown || null;
    const reservationFee = costBreakdown?.reservationFee || costBreakdown?.reservation_fee || null;
    const finalCost = session?.final_cost ?? session?.finalCost ?? session?.total_cost ?? session?.totalCost;

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservations.show.sections.linkedSession')}
                </Typography>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Box>
                        <Row label={translate('resources.reservations.linkedSession.transactionId')}>
                            <Link to={`/charging-sessions/${txnId}/show`} style={{ fontFamily: 'monospace' }}>
                                {String(txnId)}
                            </Link>
                        </Row>
                        <Row label={translate('resources.reservations.linkedSession.sessionCost')}>{fmtCurrency(finalCost)}</Row>
                        {/* Canonical 4-field shape: { held, applied, forfeited, refundReason }. No releasedToWallet. */}
                        {reservationFee && (
                            <>
                                <Row label={translate('resources.reservations.fee.held')}>{fmtCurrency(reservationFee.held)}</Row>
                                {Number(reservationFee.applied) > 0 && (
                                    <Row label={translate('resources.reservations.fee.applied')}>{fmtCurrency(reservationFee.applied)}</Row>
                                )}
                                {Number(reservationFee.forfeited) > 0 && (
                                    <Row label={translate('resources.reservations.fee.forfeited')} color="error">
                                        {fmtCurrency(reservationFee.forfeited)}
                                    </Row>
                                )}
                                {reservationFee.refundReason && (
                                    <Row label={translate('resources.reservations.fee.refundReason')} color="muted">
                                        {String(reservationFee.refundReason)}
                                    </Row>
                                )}
                            </>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

const ModificationsCard: React.FC = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {translate('resources.reservations.show.sections.modifications')}
                </Typography>
                <Row label={translate('resources.reservations.modifications.count')}>
                    {fmt(record.modification_count ?? record.modificationCount)}
                </Row>
                <Row label={translate('resources.reservations.modifications.lastModified')}>
                    {fmtDate(record.modified_at || record.modifiedAt)}
                </Row>
                {(record.cancelled_at || record.cancelledAt) && (
                    <Row label={translate('resources.reservations.modifications.cancelledAt')}>
                        {fmtDate(record.cancelled_at || record.cancelledAt)}
                    </Row>
                )}
                {(record.cancelled_by || record.cancelledBy) && (
                    <Row label={translate('resources.reservations.modifications.cancelledBy')}>
                        {fmt(record.cancelled_by || record.cancelledBy)}
                    </Row>
                )}
                {(record.cancelled_reason || record.cancelledReason) && (
                    <Row label={translate('resources.reservations.modifications.cancelledReason')}>
                        {fmt(record.cancelled_reason || record.cancelledReason)}
                    </Row>
                )}
                {(record.admin_notes || record.adminNotes) && (
                    <Row label={translate('resources.reservations.fields.adminNotes')}>
                        {fmt(record.admin_notes || record.adminNotes)}
                    </Row>
                )}
            </CardContent>
        </Card>
    );
};

export const ReservationsShow = () => (
    <Show component="div" title={false} actions={<ShowActions />}>
        <Box sx={{ width: '100%', maxWidth: 1200, margin: '0 auto', p: { xs: 1, sm: 2 } }}>
            <PageHeader />
            <ActionsCard />
            <SummaryCard />
            <PolicySnapshotCard />
            <FeeAndWalletCard />
            <LinkedSessionCard />
            <ModificationsCard />
        </Box>
    </Show>
);

export default ReservationsShow;
